import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { useCopyToClipboard } from '@sohanemon/utils/hooks';
import { m } from '@sohanscript/i18n';
import { formatBytes, formatNumber } from '@sohanscript/lib/utils/format';
import { buttonVariants } from '@sohanscript/ui/button-variants';
import { CopyButton } from '@sohanscript/ui/copy-button';
import { Label } from '@sohanscript/ui/label';
import { List, ListItem } from '@sohanscript/ui/list';
import { P } from '@sohanscript/ui/text';
import type * as React from 'react';
import { toast } from 'sonner';
import { Skeleton } from '../../skeleton';
import { useImageInspector } from '../../use-image-inspector';
import {
	type InspectorStatus,
	PALETTE_SKELETON,
	useCopyFeedback,
} from './image-inspector-shared';

export function InspectorSection({
	title,
	action,
	className,
	children,
}: {
	title: string;
	action?: React.ReactNode;
	className?: string;
	children: React.ReactNode;
}) {
	return (
		<section
			className={cn(
				'border-border/50 border-b px-4 py-4 last:border-b-0',
				className,
			)}
		>
			<div className="mb-3 flex min-h-5 items-center justify-between gap-2">
				<Label as="h3">
					<span className="text-primary">~/</span> {title}
				</Label>
				{action}
			</div>
			{children}
		</section>
	);
}

function DetailRow({ label, value }: { label: string; value: string | null }) {
	if (!value) return null;
	return (
		<ListItem
			as="div"
			className="flex items-baseline justify-between gap-4 py-2"
		>
			<dt className="shrink-0 font-mono text-2xs text-muted-foreground uppercase tracking-widest">
				{label}
			</dt>
			<dd className="min-w-0 wrap-break-word text-right font-mono text-xs text-foreground-light tabular-nums">
				{value}
			</dd>
		</ListItem>
	);
}

/** Sidebar header: live state and format. */
export function StatusStrip({
	status,
	format,
}: {
	status: InspectorStatus;
	format: string | null;
}) {
	const label =
		status === 'reading'
			? m.image_inspector_status_reading()
			: status === 'partial'
				? m.image_inspector_status_partial()
				: m.image_inspector_status_ready();
	const dot =
		status === 'ready'
			? 'bg-success'
			: status === 'partial'
				? 'bg-primary'
				: 'bg-secondary';
	const tone =
		status === 'ready'
			? 'text-success'
			: status === 'partial'
				? 'text-primary'
				: 'text-muted-foreground';
	const isLive = status !== 'ready';

	return (
		<div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-border/60 border-b px-4 py-2 font-mono text-2xs uppercase tracking-widest">
			<span className={cn('flex items-center gap-2', tone)}>
				{isLive ? (
					<span className="relative flex size-1.5">
						<span
							className={cn(
								'absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 motion-reduce:animate-none',
								dot,
							)}
						/>
						<span
							className={cn('relative inline-flex size-1.5 rounded-full', dot)}
						/>
					</span>
				) : (
					<span className={cn('size-1.5 rounded-full', dot)} />
				)}
				{label}
			</span>
			{format && <span className="text-foreground-light">{format}</span>}
		</div>
	);
}

/** Single palette entry: the swatch is the button, the hex is the label. */
function Swatch({ color }: { color: string }) {
	const { copy } = useCopyToClipboard({ timeout: 2000 });

	const handleCopy = () => {
		if (!navigator.clipboard?.writeText) {
			toast.error(m.image_inspector_clipboard_unavailable());
			return;
		}
		copy(color);
		toast.success(m.image_inspector_copied({ label: color }));
	};

	return (
		<button
			type="button"
			onClick={handleCopy}
			aria-label={m.image_inspector_copy_aria({ label: color })}
			className="group/swatch relative aspect-square w-full min-w-0 overflow-hidden rounded-md ring-1 ring-border/60 transition-shadow duration-300 hover:shadow-(--shadow-glow)"
		>
			<span className="absolute inset-0" style={{ backgroundColor: color }} />
		</button>
	);
}

export function PaletteSection({
	palette,
	isLoading,
}: {
	palette: string[];
	isLoading: boolean;
}) {
	const copyFeedback = useCopyFeedback();

	if (isLoading && palette.length === 0) {
		return (
			<InspectorSection title={m.image_inspector_palette()}>
				<div className="flex flex-wrap gap-2">
					{PALETTE_SKELETON.map((key) => (
						<Skeleton
							key={key}
							className="aspect-square max-lg:max-w-24 min-w-14 rounded-md"
						/>
					))}
				</div>
			</InspectorSection>
		);
	}

	if (palette.length === 0) return null;

	const cssVars = palette
		.map((color, index) => `--color-${index + 1}: ${color};`)
		.join(' ');

	return (
		<InspectorSection
			title={m.image_inspector_palette()}
			action={
				<CopyButton
					noIcon
					text={cssVars}
					label={m.image_inspector_copy_css_vars()}
					aria-label={m.image_inspector_copy_aria({
						label: m.image_inspector_copy_css_vars(),
					})}
					{...copyFeedback}
					className="font-mono tracking-widest uppercase"
				>
					{m.image_inspector_copy_css_vars()}
				</CopyButton>
			}
		>
			<div className="flex flex-wrap gap-2">
				{palette.map((color) => (
					<div
						key={color}
						className="flex min-w-14 max-lg:max-w-24 flex-col items-center gap-1"
					>
						<Swatch color={color} />
						<span className="w-full truncate text-center font-mono text-3xs text-muted-foreground">
							{color}
						</span>
					</div>
				))}
			</div>
		</InspectorSection>
	);
}

export function SpecsSection({
	data,
}: {
	data: ReturnType<typeof useImageInspector>['data'];
}) {
	const size = formatBytes(data.size);
	const dimensions =
		data.width && data.height
			? m.image_inspector_dimensions_value({
					width: formatNumber(data.width, { useGrouping: false }),
					height: formatNumber(data.height, { useGrouping: false }),
				})
			: null;
	const aspect =
		data.aspectLabel && data.aspectDecimal
			? `${data.aspectLabel} (${data.aspectDecimal}:1)`
			: null;

	const specs: { label: string; value: string | null }[] = [
		{ label: m.image_inspector_dimensions(), value: dimensions },
		{ label: m.image_inspector_aspect_ratio(), value: aspect },
		{ label: m.image_inspector_weight(), value: size },
		{ label: m.image_inspector_color_space(), value: data.colorSpace },
	].filter((spec) => Boolean(spec.value));

	if (specs.length === 0) return null;

	return (
		<InspectorSection title={m.image_inspector_image()}>
			<List as="dl">
				{specs.map((spec) => (
					<DetailRow key={spec.label} label={spec.label} value={spec.value} />
				))}
			</List>
			{data.isLimited && (
				<P className="mt-3 border-border/40 border-t pt-3 text-2xs text-muted-foreground leading-relaxed">
					{m.image_inspector_limited()}
				</P>
			)}
		</InspectorSection>
	);
}

export function ExposureSection({
	exif,
}: {
	exif: NonNullable<ReturnType<typeof useImageInspector>['data']['exif']>;
}) {
	const metrics: { label: string; value: string | null }[] = [
		{ label: m.image_inspector_aperture(), value: exif.aperture },
		{ label: m.image_inspector_shutter(), value: exif.shutter },
		{ label: m.image_inspector_iso(), value: exif.iso },
		{ label: m.image_inspector_focal_length(), value: exif.focalLength },
	].filter((metric) => Boolean(metric.value));

	return (
		<InspectorSection title={m.image_inspector_exposure()}>
			{metrics.length > 0 && (
				<List
					as="dl"
					className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border"
				>
					{metrics.map((metric) => (
						<ListItem
							as="div"
							key={metric.label}
							className="min-w-0 border-t-0 bg-card px-3 py-2"
						>
							<dt className="font-mono text-2xs text-muted-foreground uppercase tracking-widest">
								{metric.label}
							</dt>
							<dd className="mt-1 font-mono text-xs text-foreground-light tabular-nums">
								{metric.value}
							</dd>
						</ListItem>
					))}
				</List>
			)}
			<List as="dl" className={cn(metrics.length > 0 && 'mt-1')}>
				<DetailRow label={m.image_inspector_camera()} value={exif.camera} />
				<DetailRow label={m.image_inspector_lens()} value={exif.lens} />
				<DetailRow
					label={m.image_inspector_captured()}
					value={exif.capturedAt}
				/>
				<DetailRow label={m.image_inspector_metering()} value={exif.metering} />
			</List>
		</InspectorSection>
	);
}

export function GpsSection({
	gps,
}: {
	gps: NonNullable<ReturnType<typeof useImageInspector>['data']['gps']>;
}) {
	return (
		<InspectorSection title={m.image_inspector_location()}>
			<div className="flex items-start gap-2.5 rounded-lg border border-border/60 bg-card p-3">
				<Iconify
					icon="lucide:map-pin"
					className="mt-0.5 size-3.5 shrink-0 text-primary"
					aria-hidden="true"
				/>
				<div className="min-w-0">
					<p className="font-mono text-xs text-foreground-light">{gps.dms}</p>
					<p className="mt-0.5 font-mono text-2xs text-muted-foreground tabular-nums">
						{gps.decimal}
					</p>
				</div>
			</div>
			<a
				href={gps.osmUrl}
				target="_blank"
				rel="noreferrer"
				className={cn(
					buttonVariants({ variant: 'outline', size: 'sm' }),
					'mt-3 w-full',
				)}
			>
				<Iconify
					icon="lucide:map-pin"
					className="size-3.5"
					aria-hidden="true"
				/>
				{m.image_inspector_open_map()}
				<Iconify
					icon="lucide:arrow-up-right"
					className="size-3.5"
					aria-hidden="true"
				/>
			</a>
		</InspectorSection>
	);
}
